// VIBRANIAM-SPACE v2 - useFullscreen.js
// Wraps the standard Fullscreen API for one element. Safari still needs the
// -webkit- prefixed names, so both are checked throughout.

import { useCallback, useEffect, useState } from 'react';

function fsElement() {
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

export function useFullscreen(targetRef) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () => setIsFullscreen(fsElement() === targetRef.current);
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, [targetRef]);

  const enter = useCallback(async () => {
    const el = targetRef.current;
    if (!el) return;
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } catch {
      /* denied or unsupported - button just stays a no-op */
    }
  }, [targetRef]);

  const exit = useCallback(async () => {
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } catch {
      /* nothing was fullscreen - fine */
    }
  }, []);

  const toggle = useCallback(() => {
    if (fsElement() === targetRef.current) exit();
    else enter();
  }, [targetRef, enter, exit]);

  return { isFullscreen, toggle, enter, exit };
}

export default useFullscreen;
